import { consume } from '@lit/context';
import { localized, t } from '@weavedev/lit-i18next';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import { OrderItemDto2 } from '@/api-sdk';
import { iconPdfFilled } from '@/components/icons';
import { global, GlobalComponentType } from '@/context';
import { BREAKPOINT_SMALL_PX } from '@/settings';
import { baseStyles, formStyles, rootVariablesStyles } from '@/styles';

import '../../configuration/fg-configuration';

@customElement('fg-order-history-item')
@localized()
export class FgOrderHistoryItem extends LitElement {
  @property({ type: Object })
  orderItem: OrderItemDto2 | null = null;

  @property({ type: Boolean })
  showConfigurationDetails = false;

  @property({ type: String })
  storeCode?: string;

  @state()
  actions: { label: string; url?: string | null }[] = [];

  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  connectedCallback() {
    super.connectedCallback();

    this.actions = [
      {
        label: 'labels.packingSlip',
        url: this.orderItem?.packingSlipUrl,
      },
      {
        label: 'labels.invoice',
        url: this.orderItem?.invoiceUrl,
      },
    ];
  }

  renderActions() {
    return this.actions
      .map(action => {
        if (!action?.url) {
          return;
        }
        return html`
          <a class="action-button" href=${action?.url} target="_blank">
            ${iconPdfFilled}
            <span class="action-label">${t(action.label)}</span>
          </a>
        `;
      })
      .filter(Boolean);
  }

  render() {
    const noTopContainer =
      !this.orderItem?.configuration?.orderStatus &&
      this.renderActions()?.length === 0;

    return html`
      <div class="container">
        ${noTopContainer
          ? nothing
          : html`
              <div class="top-container">
                ${this.orderItem?.configuration?.orderStatus
                  ? html`
                      <div class="status-label">
                        ${this.orderItem?.configuration?.orderStatus}
                      </div>
                    `
                  : nothing}
                <div class="actions-container">${this.renderActions()}</div>
              </div>
            `}
        <div class="configuration-container">
          <fg-configuration
            part=${this.globalContext.layout}
            layout=${this.globalContext.layout}
            hideOrderStatus
            .locale=${this.globalContext.locale}
            .preloadedConfiguration=${this.orderItem?.configuration}
            .configuration=${this.orderItem?.configuration.uuid}
            .showConfigurationDetails=${this.showConfigurationDetails}
            .storeCode=${this.storeCode}
          ></fg-configuration>
        </div>
      </div>
    `;
  }
  static styles = [
    baseStyles,
    formStyles,
    rootVariablesStyles,
    css`
      .status-label {
        border-radius: var(--fg-borderradius--xl);
        background-color: var(--fg-gray--200, var(--fg-fallback-gray--200));
        padding-top: 5px;
        padding-bottom: 5px;
        padding-left: 16px;
        padding-right: 16px;
        color: var(--fg-gray--800, var(--fg-fallback-gray--800));
        font-family: var(
          --fg-font-family--serif,
          var(--fg-fallback-font-family--serif)
        );
        width: fit-content;
        height: 32px;
        font-weight: 700;
        line-height: 160%;
      }

      .action-label {
        font-size: 16px;
        font-weight: 700;
        padding-left: 5px;
        font-family: var(
          --fg-font-family--serif,
          var(--fg-fallback-font-family--serif)
        );
      }

      :host([part='retail']) .action-button {
        color: var(--fg-primary, var(--fg-fallback-primary));
      }

      :host([part='wholesale']) .action-button {
        color: var(--fg-secondary, var(--fg-fallback-secondary));
      }

      .action-button {
        display: flex;
        align-items: center;
        margin-right: 23px;
        text-decoration: none;

        @media screen and (max-width: ${BREAKPOINT_SMALL_PX}px) {
          && {
            margin-top: 10px;
            margin-right: 0px;
            margin-left: 16px;
          }
        }
      }

      .actions-container {
        display: flex;
        flex-direction: row;
      }

      .top-container {
        background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));
        padding-top: 12px;
        padding-bottom: 12px;
        padding-left: 10px;
        display: flex;
        align-items: center;
        justify-content: space-between;

        @media screen and (max-width: ${BREAKPOINT_SMALL_PX}px) {
          && {
            flex-direction: column;
            align-items: start;
          }
        }
      }

      .container {
        background-color: #fff;
        margin-top: 16px;
        border-radius: var(--fg-borderradius);
        border: 1px solid #d6dae1;
        overflow: hidden;
      }

      .configuration-container {
        padding: 12px;
      }
    `,
  ];
}
